import React, { useState } from 'react';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPAInput from '@/components/admin/SysadminSPA/shared/SPAInput';
import SPASelect from '@/components/admin/SysadminSPA/shared/SPASelect';
import compactSelectStyles from '@/components/admin/SysadminSPA/shared/compactSelectStyles';
import cn from '@/utils/cn';
import type { NoteValues } from './useSlideStatuses';

interface Option { label: string; value: string }

interface NoteFormProps {
  isSaving: boolean;
  onSubmit: (values: NoteValues) => void;
  statuses: string[];
}

const labelClass = 'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-spa-secondary';

// The SPA input marks focus with a border colour only, which is easy to lose on a white dialog;
// match the ring the dialog's buttons use so a keyboard user can see where they are.
export const focusRingClass = cn(
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
  'focus-visible:tw-outline-offset-2',
);

export const STATUS_INPUT_ID = 'slide-note-status';

const NoteForm = ({ isSaving, onSubmit, statuses }: NoteFormProps) => {
  const options: Option[] = statuses.map(status => ({ label: status, value: status }));
  const [status, setStatus] = useState<Option | null>(null);
  const [note, setNote] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!status) return;

    onSubmit({ note: note.trim(), status: status.value });
  };

  return (
    <form className="tw-grid tw-grid-cols-[9rem_1fr_auto] tw-items-end tw-gap-2" onSubmit={handleSubmit}>
      <div className="tw-flex tw-flex-col tw-gap-1">
        <label className={labelClass} htmlFor={STATUS_INPUT_ID}>Assignee</label>
        {/* No menuPortalTarget: the modal dialog puts pointer-events:none on <body>, so a
            portalled menu renders but never receives clicks. The dialog overflows instead. */}
        <SPASelect<Option>
          inputId={STATUS_INPUT_ID}
          maxMenuHeight={240}
          options={options}
          placeholder="Assignee"
          styles={compactSelectStyles}
          value={status}
          onChange={setStatus}
        />
      </div>
      <div className="tw-flex tw-flex-col tw-gap-1">
        <label className={labelClass} htmlFor="slide-note-text">Note</label>
        <SPAInput
          className={focusRingClass}
          id="slide-note-text"
          placeholder="Enter a note (tip: include who it is directed to, if applicable)"
          value={note}
          onChange={event => setNote(event.target.value)}
        />
      </div>
      <SPAButton className="tw-h-[2.125rem]" disabled={!status || isSaving} size="sm" type="submit" variant="primary">
        Add note
      </SPAButton>
    </form>
  );
};

export default NoteForm;
